// ══════════════════════════════════════════════
//  TimeInput — hora propia del sistema (2026-09-10), hermana de DateInput.
//  Sustituye al <input type="time"> del navegador. Reglas:
//  - Teclear manda: HH:MM en 24 h con máscara tolerante ("0930", "930",
//    "9:30", "9h30"); emite en cuanto hay minutos completos, y al salir
//    completa lo que falte ("9" es 09:00, "9:3" es 09:30). ↑/↓ mueven
//    5 minutos, con Shift una hora; Alt+↓ abre el apoyo.
//  - El botón abre un popover por portal (Z.popover + MenuScrim) con las
//    24 horas y los minutos de 5 en 5: la hora elegida se queda abierta
//    hasta elegir minutos; Borrar y Ahora al pie.
//  - `compact` es la variante de celda de captura (estilo vía `inputStyle`).
//  Valor: 'HH:MM' o ''. Nunca emite una hora inválida.
// ══════════════════════════════════════════════
import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { C, T, R, E, F, M, Z } from '../../design.js';
import { Icon } from './Icon.js';
import { MenuScrim } from './menus.js';
import { useEscToClose } from './hooks.js';
import {
  HORAS, MINUTOS, esHora, formatearHora, mascaraHora, parsearHora, horaCompleta, sumarMinutos, ahora,
} from './hora.logic.js';

const ANCHO = 236;
const ALTO_ESTIMADO = 262;

const Celda = ({ children, activo, onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className={activo ? 'gb-press gb-i gb-focus' : 'gb-ghost gb-press gb-i gb-focus'}
    style={{ height: 26, padding: 0, border: 'none', borderRadius: R.ctl, cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 12, ...F.num, fontWeight: activo ? 700 : 500,
      background: activo ? C.primaryFill : 'transparent', color: activo ? C.onPrimary : C.text }}>
    {children}
  </button>
);

const BotonPie = ({ children, onClick, disabled = false, primario = false }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`${primario ? 'gb-tint' : 'gb-ghost'} gb-press gb-i gb-focus`}
    style={{ ...T.caption, fontWeight: 600, padding: '4px 10px', border: 'none', borderRadius: R.ctl,
      background: 'transparent', fontFamily: 'inherit',
      color: disabled ? C.textDisabled : primario ? C.primary : C.textMed,
      cursor: disabled ? 'default' : 'pointer' }}>
    {children}
  </button>
);

const TimeInput = ({
  value, onChange, label, required = false, error, placeholder = 'hh:mm',
  disabled = false, compact = false, inputRef, inputStyle = {}, style: sx = {},
  onKeyDown, onFocus, onBlur, title,
}) => {
  const [texto, setTexto] = useState(() => formatearHora(value));
  const [open,  setOpen]  = useState(false);
  const [pos,   setPos]   = useState(null);
  const editando = useRef(false);
  const wrapRef  = useRef(null);
  const inputEl  = useRef(null);

  useEffect(() => { if (!editando.current) setTexto(formatearHora(value)); }, [value]);

  const setRefs = (el) => {
    inputEl.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) inputRef.current = el;
  };
  const actual = () => (esHora(formatearHora(value)) ? formatearHora(value) : '');
  const emitir = (v) => { if ((v || '') !== actual()) onChange?.(v || ''); };
  const fijar = (v) => { editando.current = false; setTexto(formatearHora(v)); emitir(v); };

  const alTeclear = (e) => {
    editando.current = true;
    const t = mascaraHora(e.target.value);
    setTexto(t);
    if (!t) { emitir(''); return; }
    if (horaCompleta(t)) { const v = parsearHora(t); if (v) emitir(v); }
  };
  const alSalir = (e) => {
    editando.current = false;
    const v = texto ? parsearHora(texto) : null;
    if (!texto) emitir('');
    setTexto(formatearHora(v || (texto ? actual() : '')));
    if (v) emitir(v);
    onBlur?.(e);
  };
  const alTecla = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); abrir(); return; }
      e.preventDefault();
      const paso = (e.shiftKey ? 60 : 5) * (e.key === 'ArrowUp' ? 1 : -1);
      fijar(sumarMinutos(actual() || ahora(), paso));
      return;
    }
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); return; }
    if (e.key === 'Enter') {
      const v = texto ? parsearHora(texto) : null;
      if (v) fijar(v);
    }
    onKeyDown?.(e);
  };

  const abrir = () => {
    if (disabled) return;
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return;
    const vh = window.innerHeight, vw = window.innerWidth;
    const abajo = vh - r.bottom - 12 >= ALTO_ESTIMADO || (vh - r.bottom) >= r.top;
    setPos({
      top:    abajo ? r.bottom + 4 : undefined,
      bottom: abajo ? undefined : vh - r.top + 4,
      left:   Math.max(8, Math.min(r.left, vw - ANCHO - 8)),
    });
    setOpen(true);
  };
  const cerrar = useCallback(() => setOpen(false), []);
  useEscToClose(open ? cerrar : null);

  const [hSel, mSel] = (actual() || ':').split(':');
  // Hora elegida: conserva los minutos y se queda abierto; minuto elegido: cierra
  const elegirHora = (h) => fijar(`${h}:${mSel || '00'}`);
  const elegirMinuto = (m) => {
    fijar(`${hSel || ahora().slice(0, 2)}:${m}`);
    setOpen(false);
    setTimeout(() => inputEl.current?.focus(), 0);
  };
  const elegir = (v) => { fijar(v); setOpen(false); setTimeout(() => inputEl.current?.focus(), 0); };

  const campo = (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', alignItems: 'center',
      zIndex: open ? Z.popover : undefined, ...(label ? {} : sx) }}>
      <input ref={setRefs} value={texto} onChange={alTeclear} onKeyDown={alTecla}
        onFocus={onFocus} onBlur={alSalir}
        placeholder={placeholder} disabled={disabled} inputMode="numeric" maxLength={5}
        title={title} aria-invalid={error ? true : undefined}
        className="gb-i gb-focus"
        style={compact
          ? { flex: 1, minWidth: 0, ...F.num, ...inputStyle }
          : { flex: 1, minWidth: 0, boxSizing: 'border-box', padding: '8px 30px 8px 10px',
              borderRadius: R.ctl, border: `1px solid ${error ? C.errorFieldBorder : C.border}`,
              fontSize: 13, color: C.text, fontFamily: 'inherit', ...F.num,
              background: error ? C.errorField : C.surface, ...inputStyle }}/>
      <button type="button" tabIndex={-1} disabled={disabled}
        onClick={() => (open ? setOpen(false) : abrir())}
        title="Elegir la hora" aria-label="Elegir la hora"
        aria-haspopup="dialog" aria-expanded={open}
        className="gb-ghost gb-press gb-i"
        style={{ position: compact ? 'static' : 'absolute', right: compact ? undefined : 4,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: compact ? 20 : 24, height: compact ? 20 : 24, padding: 0, border: 'none',
          borderRadius: R.xs, flexShrink: 0,
          background: open ? C.primaryLight : 'transparent',
          color: open ? C.primary : C.textMuted, cursor: disabled ? 'default' : 'pointer' }}>
        <Icon name="clock" size={compact ? 12 : 14} color="currentColor"/>
      </button>
    </div>
  );

  const panel = open && pos && createPortal(
    <>
      <MenuScrim onClose={cerrar} zIndex={Z.popover - 1}/>
      <div role="dialog" aria-label="Hora"
        style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: ANCHO,
          zIndex: Z.popover, background: C.flotante, border: `1px solid ${C.border}`,
          borderRadius: R.menu, boxShadow: E.menu, padding: 10, boxSizing: 'border-box',
          animation: `gbFade ${M.fast} ${M.ease}` }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'start' }}>
          <div>
            <div style={{ ...T.micro, fontWeight: 600, color: C.textMuted, marginBottom: 4 }}>Hora</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
              {HORAS.map(h => <Celda key={h} activo={h === hSel} onClick={() => elegirHora(h)}>{h}</Celda>)}
            </div>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', background: C.borderLight, marginTop: 18 }}/>
          <div>
            <div style={{ ...T.micro, fontWeight: 600, color: C.textMuted, marginBottom: 4 }}>Minutos</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
              {MINUTOS.map(m => <Celda key={m} activo={m === mSel} onClick={() => elegirMinuto(m)}>{m}</Celda>)}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <BotonPie onClick={() => elegir('')} disabled={!actual()}>Borrar</BotonPie>
          <BotonPie onClick={() => elegir(ahora())} primario>Ahora</BotonPie>
        </div>
      </div>
    </>,
    document.body,
  );

  if (label) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, ...sx }}>
        <label style={{ ...T.caption, color: C.textSub }}>
          {label}{required && <span style={{ color: C.danger }}> *</span>}
        </label>
        {campo}
        {typeof error === 'string' && error && <span style={{ ...T.micro, color: C.danger }}>{error}</span>}
        {panel}
      </div>
    );
  }
  return <>{campo}{panel}</>;
};

export { TimeInput };
